import React, {useEffect, useRef} from 'react';
import {Box, Button, Drawer, IconButton, InputBase, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import {useTranslation} from 'react-i18next';

/** Finger-sized bordered round button for the font − / + steppers. */
const stepperSx = {
    color: '#C3F3FD',
    border: '1.5px solid #2ea6e6',
    backgroundColor: '#071826',
    width: 34,
    height: 34,
    flexShrink: 0,
} as const;

// Anchored to the TOP: a top panel is never covered by the keyboard, so there
// is no keyboard-chasing (and none of its jumping). The board stays visible in
// the band between this panel and the keyboard; the edited element is scrolled
// into that band by the parent.
const PaperSx = {
    borderRadius: '0 0 18px 18px',
    background: 'linear-gradient(220deg, #051726 36%, #025989 100%)',
    padding: 2,
    // Same reason as the image sheet: the layer style + z-order rows can push
    // this past the visible viewport, and a clipped control is an absent one.
    maxHeight: '85vh',
    overflowY: 'auto',
    // Clear the status bar/notch so the label isn't tucked under it.
    paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
    // The Modal root below is pointer-events:none so the page behind stays
    // scrollable/tappable; the sheet itself must take its own taps.
    pointerEvents: 'auto',
};

// Non-modal sheet: no backdrop dim, no scroll lock, no focus trap — the canvas
// below stays visible AND the page scrolls behind it. The invisible Modal root
// must not eat scroll/taps, so it's pointer-events:none (Paper re-enables them).
// zIndex above the app's own top bar so the panel covers it while editing.
const NonModalProps = {
    hideBackdrop: true,
    disableScrollLock: true,
    disableEnforceFocus: true,
    disableAutoFocus: true,
    sx: {pointerEvents: 'none', zIndex: 2000},
};

interface TextEditSheetProps {
    open: boolean;
    label: string;
    value: string;
    /** Single-line max length; when set the header shows an n/max counter. */
    maxChars?: number;
    multiline?: boolean;
    /** Fires on every keystroke so the canvas above updates live. */
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    onSave: () => void;
    onClose: () => void;
    /** Font − / + steppers (±10% per tap). Absent → hidden (legacy HTML designs). */
    onFontStep?: (direction: 1 | -1) => void;
    /** Extra controls under the save button — the user-layer colour/alignment
     *  and z-order/delete clusters. A ReactNode rather than a dozen optional
     *  callbacks: the layer control set lives in ONE component shared with the
     *  desktop panel, so the two surfaces cannot drift apart. */
    extraControls?: React.ReactNode;
}

/** Bottom sheet for a text field. The canvas stays visible above it, so the
 *  user sees each keystroke land on the design (onChange writes straight to the
 *  parent's selections). Closing keeps the typed value — there is no revert. */
const TextEditSheet: React.FC<TextEditSheetProps> = ({
                                                         open,
                                                         label,
                                                         value,
                                                         maxChars,
                                                         multiline,
                                                         onChange,
                                                         onSave,
                                                         onClose,
                                                         onFontStep,
                                                         extraControls,
                                                     }) => {
    const {t} = useTranslation();
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const paperRef = useRef<HTMLDivElement>(null);

    // Autofocus once the sheet has opened (Drawer mounts its content on open).
    // preventScroll stops iOS from auto-scrolling to "reveal" the input — the
    // parent handles positioning the edited element into view instead.
    useEffect(() => {
        if (!open) {
            // Closing: blur the input so iOS dismisses the keyboard (unmounting
            // a focused field alone doesn't reliably close it). Blur the active
            // element too in case focus drifted (e.g. to the keyboard primer).
            inputRef.current?.blur();
            (document.activeElement as HTMLElement | null)?.blur();
            return;
        }
        const id = window.setTimeout(() => inputRef.current?.focus({preventScroll: true}), 60);
        return () => window.clearTimeout(id);
    }, [open]);

    // Pin the top panel to the top of the VISIBLE viewport. On iOS the keyboard
    // shifts visualViewport.offsetTop (it scrolls the visual viewport within the
    // layout viewport), and a position:fixed top:0 element sits at the LAYOUT
    // top — which then scrolls above the visible area and hides. Track offsetTop
    // and translate the panel down to compensate. Inline style (bypasses RTL
    // emotion cache); MUI owns the open-slide transform on the paper's own
    // element, so we set it on the paper too but only when idle (offsetTop 0 →
    // empty string, letting MUI's slide play).
    useEffect(() => {
        if (!open) return;
        const vv = window.visualViewport;
        if (!vv) return;
        const sync = () => {
            const paper = paperRef.current;
            if (!paper) return;
            const off = Math.round(vv.offsetTop);
            paper.style.top = off ? `${off}px` : '';
        };
        sync();
        vv.addEventListener('resize', sync);
        vv.addEventListener('scroll', sync);
        return () => {
            vv.removeEventListener('resize', sync);
            vv.removeEventListener('scroll', sync);
            if (paperRef.current) paperRef.current.style.top = '';
        };
    }, [open]);

    return (
        <Drawer
            anchor="top"
            open={open}
            onClose={onClose}
            ModalProps={NonModalProps}
            PaperProps={{sx: PaperSx, ref: paperRef}}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.25}}>
                <Typography sx={{color: 'white', fontWeight: 700, fontSize: '1.05rem', flexGrow: 1}} noWrap>
                    {label}
                </Typography>
                {/* Font − / + (scene designs only): each tap nudges the edited
                    element ±10%, live on the canvas above. pointerdown-preventDefault
                    keeps focus in the input so the keyboard never dips. */}
                {onFontStep && (
                    <>
                        <IconButton
                            data-testid="fill-font-minus"
                            size="small"
                            aria-label={t('editor.imageSelector.fontDecrease')}
                            onPointerDown={(e) => e.preventDefault()}
                            onClick={() => onFontStep(-1)}
                            sx={stepperSx}
                        >
                            <RemoveIcon fontSize="small"/>
                        </IconButton>
                        <IconButton
                            data-testid="fill-font-plus"
                            size="small"
                            aria-label={t('editor.imageSelector.fontIncrease')}
                            onPointerDown={(e) => e.preventDefault()}
                            onClick={() => onFontStep(1)}
                            sx={stepperSx}
                        >
                            <AddIcon fontSize="small"/>
                        </IconButton>
                    </>
                )}
                {maxChars !== undefined && (
                    <Typography sx={{color: '#7fa1ba', fontSize: '0.8rem', fontWeight: 600, flexShrink: 0}}>
                        {value.length}/{maxChars}
                    </Typography>
                )}
                <IconButton onClick={onClose} size="small" aria-label={t('editor.fillMode.back')}
                            sx={{color: '#8fb0c8', flexShrink: 0, ml: 0.5}}>
                    <CloseIcon fontSize="small"/>
                </IconButton>
            </Box>

            <InputBase
                inputRef={inputRef}
                fullWidth
                multiline={multiline}
                value={value}
                onChange={onChange}
                // Kill the iOS accessory bar: no password/contact/card autofill
                // (autoComplete off + a neutral name) and no predictive/spellcheck
                // strip. It's a free-text content field, not a form credential.
                inputProps={{
                    maxLength: maxChars,
                    dir: 'auto',
                    name: 'fill-text',
                    autoComplete: 'off',
                    autoCorrect: 'off',
                    autoCapitalize: 'sentences',
                    spellCheck: false,
                    'data-1p-ignore': true,
                    'data-lpignore': true,
                }}
                sx={{
                    '& .MuiInputBase-input': {
                        backgroundColor: '#071826',
                        border: '1.5px solid #2ea6e6',
                        borderRadius: '13px',
                        color: '#fff',
                        fontSize: '17px',
                        fontWeight: 700,
                        padding: '0 15px',
                        height: multiline ? 'auto' : '52px',
                        minHeight: multiline ? '52px' : undefined,
                        boxSizing: 'border-box',
                        ...(multiline && {padding: '13px 15px'}),
                    },
                }}
            />

            <Button
                data-testid="fill-text-save"
                fullWidth
                onClick={onSave}
                sx={{
                    height: '52px',
                    borderRadius: '14px',
                    backgroundColor: '#17c47f',
                    color: '#04241a',
                    fontSize: '1rem',
                    fontWeight: 800,
                    textTransform: 'none',
                    mt: 1.5,
                    '&:hover': {backgroundColor: '#13a86c'},
                }}
            >
                {t('editor.fillMode.saveText')}
            </Button>

            {extraControls}

            {/* Drag handle at the bottom edge of the top panel; tap to close. */}
            <Box onClick={onClose}
                 sx={{width: 36, height: 4, borderRadius: 2, backgroundColor: '#ffffff50', margin: '12px auto 0', cursor: 'pointer'}}/>
        </Drawer>
    );
};

export default TextEditSheet;
